import { useEffect, useState } from 'react';
import { getWorkOrderDetailApi, type WorkOrderDetail } from '@/api/workorder.api';

// One work order, as the operator and the production manager see it: the order and product, the
// stage and its place in the sequence, target and input quantity, machine and crew, estimated
// duration and instructions, planned time, status and the stage's materials with their issue status;
// then its time (planned against actual, pauses) and its cost (standard against actual, from the
// order's costing).

export const WO_DISPLAY_STATUS: Record<string, { label: string; cls: string; icon: string }> = {
  LOCKED: { label: 'Locked', cls: 'bg-slate-100 text-slate-500 border-slate-200', icon: 'ri-lock-line' },
  PLANNED: { label: 'Planned', cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: 'ri-calendar-line' },
  ASSIGNED: { label: 'Assigned', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: 'ri-user-follow-line' },
  IN_PROGRESS: { label: 'In Progress', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-loader-4-line' },
  PAUSED: { label: 'Paused', cls: 'bg-orange-50 text-orange-700 border-orange-200', icon: 'ri-pause-circle-line' },
  QC_PENDING: { label: 'QC Pending', cls: 'bg-purple-50 text-purple-700 border-purple-200', icon: 'ri-microscope-line' },
  COMPLETED_QC_PENDING: { label: 'QC Pending', cls: 'bg-purple-50 text-purple-700 border-purple-200', icon: 'ri-microscope-line' },
  QC_FAILED: { label: 'QC Failed', cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: 'ri-close-circle-line' },
  COMPLETED: { label: 'Completed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'ri-check-line' },
  ON_HOLD: { label: 'On Hold', cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: 'ri-pause-circle-line' },
};

export const formatDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const formatDuration = (minutes?: number | null) => {
  const m = Math.round(Number(minutes) || 0);
  if (!m) return '—';
  const h = Math.floor(m / 60);
  return h ? `${h} hr${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`;
};

export function StatusPill({ status }: { status: string }) {
  const s = WO_DISPLAY_STATUS[status] || WO_DISPLAY_STATUS.PLANNED;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${s.cls}`}>
      <i className={s.icon} /> {s.label}
    </span>
  );
}

const money = (v?: number | null) =>
  `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SectionTitle = ({ icon, children }: { icon: string; children: React.ReactNode }) => (
  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1"><i className={icon} /> {children}</p>
);

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-slate-100 last:border-0">
    <span className="text-slate-500 shrink-0">{label}</span>
    <span className="text-slate-800 text-right">{children}</span>
  </div>
);

/** The work order's facts; shared by the drawer and the operator's cards. */
export function WorkOrderFacts({ wo }: { wo: WorkOrderDetail }) {
  return (
    <>
      <section>
        <Row label="Production Order">{wo.po_number}</Row>
        <Row label="Product">{wo.item_name}{wo.item_code ? <span className="text-slate-400"> · {wo.item_code}</span> : null}</Row>
        <Row label="Stage">Stage {wo.stage_number} — {wo.stage_name}</Row>
        <Row label="Sequence">{wo.sequence.current} of {wo.sequence.total}</Row>
      </section>
      <section>
        <Row label="Target Qty">{Number(wo.planned_qty)} {wo.unit}</Row>
        <Row label="Input Qty">
          {wo.input.qty} {wo.unit}
          <span className="block text-xs text-slate-400">
            {wo.input.fromWo ? `from ${wo.input.fromWo} (${wo.input.fromStage})` : 'first stage — from issued material'}
          </span>
        </Row>
        {Number(wo.completed_qty) > 0 && <Row label="Produced">{Number(wo.completed_qty)} good · {Number(wo.rejected_qty) || 0} rejected</Row>}
      </section>
      <section>
        <Row label="Work Center">{wo.work_center_name || '—'}</Row>
        <Row label="Machine">
          {wo.machine_name ? `${wo.machine_code ? `${wo.machine_code} · ` : ''}${wo.machine_name}` : '—'}
          {wo.machine_status && <span className="block text-xs text-slate-400">{wo.machine_status === 'RUNNING' ? 'Busy' : wo.machine_status.toLowerCase()}</span>}
        </Row>
        <Row label="Assigned To">
          {wo.crew.length ? wo.crew.map((c) => c.name).join(', ') : <span className="text-slate-400">Not assigned</span>}
          {Number(wo.operators_required) > 1 && <span className="block text-xs text-slate-400">crew of {wo.operators_required}</span>}
        </Row>
        <Row label="Shift">{wo.shift_name || '—'}</Row>
        <Row label="Est. Duration">{formatDuration(wo.planned_time_minutes)}</Row>
      </section>
      <section>
        <Row label="Planned Start">{formatDateTime(wo.planned_start_at || wo.planned_start_date)}</Row>
        <Row label="Planned End">{formatDateTime(wo.planned_end_at || wo.planned_end_date)}</Row>
        {wo.actual_start_date && <Row label="Started">{formatDateTime(wo.actual_start_date)}</Row>}
      </section>
      {wo.instructions && (
        <section className="rounded-lg bg-amber-50/60 border border-amber-100 px-3 py-2">
          <p className="text-xs font-semibold text-amber-800 mb-0.5"><i className="ri-file-text-line" /> Instructions</p>
          <p className="text-sm text-slate-700 whitespace-pre-line">{wo.instructions}</p>
        </section>
      )}
      <section>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Materials (stage-wise)</p>
        {wo.materials.length === 0 ? (
          <p className="text-sm text-slate-400">No materials queued for this stage yet</p>
        ) : (
          <ul className="space-y-1">
            {wo.materials.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-slate-700">
                  {m.item_name} — {m.required_qty} {m.unit}
                  {(m.batch_allocations?.length ?? 0) > 0 && (
                    <span className="block text-xs text-slate-400">batch {m.batch_allocations!.map((b) => b.batchNumber).join(', ')}</span>
                  )}
                </span>
                {m.status === 'ISSUED' ? (
                  <span className="text-xs font-semibold text-emerald-700 whitespace-nowrap"><i className="ri-checkbox-circle-fill" /> Issued</span>
                ) : m.status === 'PARTIAL' ? (
                  <span className="text-xs font-semibold text-indigo-700 text-right whitespace-nowrap">
                    <i className="ri-contrast-2-line" /> {m.issued_qty} issued
                    <span className="block font-normal text-slate-500">{Math.max(0, m.required_qty - m.issued_qty)} {m.unit} pending</span>
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-amber-700 whitespace-nowrap"><i className="ri-time-line" /> Pending</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <TimeTracking wo={wo} />
      <CostAnalysis wo={wo} />
    </>
  );
}

/** Planned against actual time; pauses are the downtime logged against the stage. */
function TimeTracking({ wo }: { wo: WorkOrderDetail }) {
  const t = wo.time;
  if (!t) return null;
  const delay = t.delayMinutes;
  return (
    <section>
      <SectionTitle icon="ri-timer-line">Time Tracking</SectionTitle>
      <Row label="Planned">{formatDuration(t.plannedMinutes)}</Row>
      {t.startedAt ? (
        <>
          <Row label={t.endedAt ? 'Start → End' : 'Running Since'}>
            {formatDateTime(t.startedAt)}{t.endedAt ? ` → ${formatDateTime(t.endedAt)}` : ''}
          </Row>
          <Row label="Elapsed">{formatDuration(t.elapsedMinutes)}</Row>
          <Row label={`Paused${t.pauses.length ? ` (${t.pauses.length})` : ''}`}>{formatDuration(t.pausedMinutes)}</Row>
          <Row label="Net Run">{formatDuration(t.netRunMinutes)}</Row>
          {t.loggedMinutes !== null && (
            <Row label="Logged in Production Entries">{formatDuration(t.loggedMinutes)}</Row>
          )}
          {delay !== null && (
            <Row label="Against Plan">
              <span className={delay > 0 ? 'text-rose-700 font-semibold' : 'text-emerald-700 font-semibold'}>
                {delay > 0 ? `${formatDuration(delay)} late` : delay < 0 ? `${formatDuration(-delay)} early` : 'on time'}
              </span>
            </Row>
          )}
        </>
      ) : (
        <p className="text-sm text-slate-400 py-1">Not started</p>
      )}
      {t.pauses.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {t.pauses.map((p) => (
            <li key={p.entryNumber} className="flex justify-between gap-2 text-xs text-slate-600">
              <span>
                <i className={p.open ? 'ri-pause-circle-fill text-orange-500' : 'ri-pause-circle-line text-slate-400'} />{' '}
                {p.reason || p.category || 'Paused'} · {formatDateTime(p.startedAt)}
              </span>
              <span className={p.open ? 'text-orange-700 font-semibold' : ''}>{p.open ? `${formatDuration(p.minutes)} so far` : formatDuration(p.minutes)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Standard against actual cost of the stage, from the order's costing: the actual labour and machine
 * cost follow the time booked in production entries; the overhead % is on material and labour.
 */
function CostAnalysis({ wo }: { wo: WorkOrderDetail }) {
  const c = wo.cost;
  if (!c) return null;
  const lines: { label: string; std: number; act: number }[] = [
    { label: 'Material', std: c.standard.material, act: c.actual.material },
    { label: 'Labour', std: c.standard.labour, act: c.actual.labour },
    { label: 'Machine', std: c.standard.machine, act: c.actual.machine },
    { label: `Overhead${c.overheadPct ? ` (${c.overheadPct}%)` : ''}`, std: c.standard.overhead - c.standard.machine, act: c.actual.overhead - c.actual.machine },
  ];
  const started = c.actHours > 0 || c.producedQty > 0;
  const perPieceStd = c.plannedQty > 0 ? c.standard.total / c.plannedQty : null;
  const perPieceAct = c.producedQty > 0 ? c.actual.total / c.producedQty : null;
  return (
    <section>
      <SectionTitle icon="ri-money-rupee-circle-line">Cost Analysis</SectionTitle>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-slate-500 border-b border-slate-100">
            <th className="text-left font-medium py-1">Element</th>
            <th className="text-right font-medium py-1">Standard</th>
            <th className="text-right font-medium py-1">Actual</th>
            <th className="text-right font-medium py-1">Variance</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const v = l.act - l.std;
            return (
              <tr key={l.label} className="border-b border-slate-50">
                <td className="py-1 text-slate-600">{l.label}</td>
                <td className="py-1 text-right text-slate-700">{money(l.std)}</td>
                <td className="py-1 text-right text-slate-700">{started ? money(l.act) : '—'}</td>
                <td className={`py-1 text-right ${!started || Math.abs(v) < 0.005 ? 'text-slate-400' : v > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                  {started ? `${v > 0 ? '+' : ''}${money(v)}` : '—'}
                </td>
              </tr>
            );
          })}
          <tr className="font-semibold">
            <td className="py-1.5 text-slate-800">Total</td>
            <td className="py-1.5 text-right text-slate-800">{money(c.standard.total)}</td>
            <td className="py-1.5 text-right text-slate-800">{started ? money(c.actual.total) : '—'}</td>
            <td className={`py-1.5 text-right ${!started || Math.abs(c.variance) < 0.005 ? 'text-slate-400' : c.variance > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
              {started ? `${c.variance > 0 ? '+' : ''}${money(c.variance)}` : '—'}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="mt-1">
        <Row label="Hours (std / actual)">{c.stdHours.toFixed(2)} / {started ? c.actHours.toFixed(2) : '—'}</Row>
        <Row label="Cost per Piece">
          {perPieceStd !== null ? `${money(perPieceStd)} std` : '—'}
          {perPieceAct !== null && <span className="block text-xs text-slate-500">{money(perPieceAct)} actual on {c.producedQty} good</span>}
        </Row>
      </div>
    </section>
  );
}

interface Props {
  workOrderId: string | null;
  onClose: () => void;
}

export default function WorkOrderDetailDrawer({ workOrderId, onClose }: Props) {
  const [wo, setWo] = useState<WorkOrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workOrderId) return;
    let cancelled = false;
    setWo(null);
    setError(null);
    getWorkOrderDetailApi(workOrderId)
      .then((res) => { if (!cancelled) setWo(res.data || null); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || err?.message || 'Could not load the work order'); });
    return () => { cancelled = true; };
  }, [workOrderId]);

  if (!workOrderId) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md h-full shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200">
          <div>
            <p className="text-xs text-slate-400">Work Order</p>
            <h3 className="text-lg font-bold text-slate-900">{wo?.wo_number || '…'}</h3>
            {wo && <div className="mt-1"><StatusPill status={wo.display_status} /></div>}
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && <p className="text-sm text-rose-600">{error}</p>}
          {!wo && !error && <p className="text-sm text-slate-400"><i className="ri-loader-4-line animate-spin" /> Loading…</p>}
          {wo && <WorkOrderFacts wo={wo} />}
        </div>
      </div>
    </div>
  );
}
